import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useApi, useMe } from '../context';
import { getPref, getPrefList, useCallEvents } from '../realtime';
import type { CallRow } from '../types';

const AUTO_OPEN_KEY = 'hlh.autoOpenRingingCall';
const AUTO_OPEN_LINES_KEY = 'hlh.autoOpenRingingLines';

/**
 * Site-wide banner when a call is ringing. No sound (by design). Auto-open (and which lines
 * to monitor) is configured on the Lines page; this component only reads those per-viewer
 * preferences to decide whether to open a ringing call automatically.
 */
export function RingingBanner() {
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const [ringing, setRinging] = useState<CallRow[]>([]);
  const canHandle = me.roles.some((r) => r === 'on_call_user' || r === 'manager' || r === 'admin');

  const refresh = useCallback(async (): Promise<CallRow[]> => {
    const rows = await api.get<CallRow[]>('/api/calls?state=ringing&limit=10').catch(() => [] as CallRow[]);
    setRinging(rows);
    return rows;
  }, [api]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Empty monitored list = every line auto-opens; otherwise only the chosen line numbers.
  const shouldAutoOpen = useCallback((lineNumber: string | null) => {
    if (!getPref(AUTO_OPEN_KEY)) return false;
    const monitored = getPrefList(AUTO_OPEN_LINES_KEY);
    return monitored.length === 0 || (lineNumber !== null && monitored.includes(lineNumber));
  }, []);

  useCallEvents(
    useCallback(
      (type: string, data: unknown) => {
        void refresh().then((rows) => {
          if (type !== 'call.ringing' || !canHandle) return;
          const callId = (data as { callId?: string }).callId;
          const call = rows.find((r) => r.id === callId);
          // Don't yank someone off a call they already have open.
          const viewingACall = /^\/calls\/[^/]+$/.test(pathRef.current);
          if (call && shouldAutoOpen(call.line_number) && !viewingACall) navigate(`/calls/${call.id}`);
        });
      },
      [refresh, canHandle, navigate, shouldAutoOpen],
    ),
  );

  return (
    <>
      {ringing.map((c) => (
        <div key={c.id} className="ringing-banner" role="alert" onClick={() => navigate(`/calls/${c.id}`)}>
          <span className="pulse" aria-hidden="true" />
          <strong>Incoming call</strong>
          <span>{c.caller_number ?? 'Unknown number'}</span>
          {c.originating_queue_name && <span>→ {c.originating_queue_name}</span>}
          <span className="spacer" />
          <span className="open-hint">Open</span>
        </div>
      ))}
    </>
  );
}
